// Field Labels: Step 0 of a site. The yellow peel-and-stick labels that name the gear before it has an Asset ID.
// Words: FIELD_LABELS_WORDS.md (concept approved 2026-10-23, 8:16 AM; Toro Max standard 10:18 AM; fit rule 10:22 AM).
// Mockup: MOCKUP_FIELD_LABELS.html rev 1 (approved 2026-10-23, 10:27 AM).
// Pure: no database, no network, no DOM. The page (field-labels-page.ts) and the PDF (field-label-pdf.ts) share it.
//
// A label is a name on the gear until its Asset ID Creator row is complete. Nothing here makes an asset.
//
// The notepad: one line per housing, written the way the notebook reads.
//   ER1                              a room line, holds until the next room line
//   MCC-1A  A1-A3  B1-B6  B5A B5B    spelled positions; B5A B5B take B5's place
//   MCC-1B  3  6  10  SPARE E4       buckets per section; letters run on from the last letter used in the room
//   PNL-01  MB  42                   a main, then breakers B1 to B42
//   SWBD-01  1X4                     a size other than the default for this housing's label

// ---- Sizes: the Toro Max standard (height x length, in inches, the driver's way) ----

export interface LabelSize {
  /** The material: 0.5, 1 or 2 in. */
  height: number;
  /** The paper size length set in the driver. */
  length: number;
}

export type LabelKind = "breaker" | "bucket" | "housing" | "big";

/** The four paper sizes Robert set up on the Toro Max (2026-10-23, 10:18 AM). Smallest first. */
export const TORO_MAX_SIZES: readonly LabelSize[] = [
  { height: 0.5, length: 1.5 },
  { height: 1, length: 2 },
  { height: 1, length: 4 },
  { height: 2, length: 9 },
];

/** Defaults by what is labeled. Any label can change in the field. */
export const DEFAULT_SIZES: Record<LabelKind, LabelSize> = {
  breaker: { height: 0.5, length: 1.5 },
  bucket: { height: 1, length: 2 },
  housing: { height: 1, length: 4 },
  big: { height: 2, length: 9 },
};

/** The big gear: MCCs and switchboards default to 2 x 9. */
export const BIG_TYPES: readonly string[] = ["MCC", "SWBD", "SWGR"];
/**
 * Housings built in sections of spaces: MCC buckets, switchboard and switchgear compartments.
 * Letter = section, number = space top to bottom. The notepad below runs letters across all of them (12:24 PM);
 * rev 2.8 corrected that: only MCCs share a room's letters (1:07 PM). The page uses field-labels-site.ts for it.
 */
export const SECTIONED_TYPES: readonly string[] = ["MCC", "SWBD", "SWGR"];
export const isSectioned = (type: string): boolean => SECTIONED_TYPES.includes(type);
/** Housings whose children are breakers: a count after the name is the breaker count. */
export const BREAKER_TYPES: readonly string[] = ["PNL", "MDP", "MSP"];

/** The equipment codes a housing line can start with (the workbook's EET list, asset-id.ts WORKBOOK_CLASSES). */
export const HOUSING_TYPES: readonly string[] = [
  "SWBD", "SWGR", "MCC", "PNL", "XFMR", "VFD", "ICP", "DS", "FDS", "NFDS", "ATS", "ENCL", "GEN", "UPS", "PFC", "LR", "SS",
  "MD", "MSP", "MSD", "MDP", "CAP", "CP", "JBOX", "MS", "UTL", "GUT",
];

export const sizeKey = (s: LabelSize): string => `${s.height}x${s.length}`;
/** "1 x 4", as the driver lists it. */
export const sizeName = (s: LabelSize): string => `${s.height} x ${s.length}`;
export function sizeFromKey(k: string): LabelSize | null {
  return TORO_MAX_SIZES.find((s) => sizeKey(s) === k) ?? null;
}

// ---- Text: one height per material, the length follows the name ----

/** Helvetica Bold advance widths per 1000 em, the PDF standard font (same widths as Arial Bold). */
const HELVETICA_BOLD: Record<string, number> = {
  " ": 278, "-": 333, ".": 278, "(": 333, ")": 333, "/": 278,
  "0": 556, "1": 556, "2": 556, "3": 556, "4": 556, "5": 556, "6": 556, "7": 556, "8": 556, "9": 556,
  A: 722, B: 722, C: 722, D: 722, E: 667, F: 611, G: 778, H: 722, I: 278, J: 556, K: 722, L: 611, M: 833,
  N: 722, O: 778, P: 667, Q: 778, R: 722, S: 667, T: 611, U: 722, V: 667, W: 944, X: 667, Y: 667, Z: 611,
  a: 556, b: 611, c: 556, d: 611, e: 556, f: 333, g: 611, h: 611, i: 278, j: 278, k: 556, l: 278, m: 889,
  n: 611, o: 611, p: 611, q: 611, r: 389, s: 556, t: 333, u: 611, v: 556, w: 778, x: 556, y: 556, z: 500,
};
/** Helvetica Bold cap height per 1000 em. */
export const CAP_HEIGHT = 0.718;
/** 64 pt on 1 in material: capitals about 0.64 in tall. Scales with the material. */
export const POINTS_PER_INCH_OF_MATERIAL = 64;
/** Yellow at each end, as a share of the material height. */
export const END_PAD = 0.15;
/** Text may shrink this far to stay on a size (approved 2026-10-23, 10:22 AM). */
export const MAX_SHRINK = 0.1;

/** Text width in inches at a font size in points. */
export function textWidthIn(text: string, points: number): number {
  let em = 0;
  for (const ch of text) em += HELVETICA_BOLD[ch] ?? 722;
  return (em / 1000) * (points / 72);
}

export interface Fit {
  /** The font size in points the label is drawn at. */
  points: number;
  /** The length the name needs at full height, inches. */
  need: number;
  /** 1 at full height. Under 1 when the text shrinks to fit. */
  scale: number;
  /** Within the 10% shrink. */
  ok: boolean;
}

/** How a name sits on a size: full height when it fits, shrunk to fit otherwise. */
export function fitOf(text: string, size: LabelSize): Fit {
  const full = POINTS_PER_INCH_OF_MATERIAL * size.height;
  const need = textWidthIn(text, full) + 2 * END_PAD * size.height;
  const scale = Math.min(1, size.length / need);
  return { points: full * scale, need, scale, ok: scale >= 1 - MAX_SHRINK - 1e-9 };
}

/**
 * A split bucket on a 2-digit space (B10a, C12b) is too long for 1 x 2 at full height. Robert, 2026-10-27, 12:13 PM:
 * keep 1 x 2, the smaller height is fine as long as they print (about 83%). 12:17 PM: the warning stays quiet for them.
 * It still speaks up below 80%, on wide letters like M12d or W12d.
 */
export const SPLIT_MIN_SCALE = 0.8;

/** Holds on its size: within the 10% shrink, or a split bucket at 80% or more. */
export function holdsOn(label: Pick<FieldLabel, "text" | "kind">, size: LabelSize): boolean {
  const f = fitOf(label.text, size);
  return f.ok || (label.kind === "bucket" && /[a-z]$/.test(label.text) && f.scale >= SPLIT_MIN_SCALE - 1e-9);
}

/** The shortest standard size this name fits within the 10% shrink, at least as tall as the one it is on. */
export function nextSizeUp(text: string, size: LabelSize): LabelSize | null {
  return TORO_MAX_SIZES.find((s) => s.height >= size.height && (s.height > size.height || s.length > size.length) && fitOf(text, s).ok) ?? null;
}

// ---- The lineup ----

export interface FieldLabel {
  /** Stable while the notepad is edited: room | housing | position. A housing's own label ends in "|". */
  key: string;
  room: string;
  housing: string;
  /** What prints: "MCC-1A", "C3", "B12", "MB". */
  text: string;
  kind: LabelKind;
  /** Section number for a bucket, 1 up. */
  section: number | null;
  spare: boolean;
}

export interface Housing {
  room: string;
  name: string;
  /** "MCC", "PNL". */
  type: string;
  /** The housing's own label. */
  label: FieldLabel;
  /** Buckets or breakers, top to bottom, section by section. */
  kids: FieldLabel[];
  /** A size written on the line (1X4). Null: the default or a tap in the preview. */
  lineSize: LabelSize | null;
  main: boolean;
  /** Notepad line, 1 up: the line with the housing's name. */
  line: number;
  /** The last notepad line that belongs to it: buckets written one per line carry on under the name. */
  lastLine: number;
}

export interface Room {
  name: string;
  housings: Housing[];
  /** The last notepad line with this room's name. 0 for a housing written before any room. */
  line: number;
}

export interface Problem {
  /** Red: a lockout risk. Amber: check it. */
  tone: "red" | "amber";
  text: string;
  line: number;
  /** The label the problem sits on, when there is one. */
  key?: string;
}

export interface Lineup {
  rooms: Room[];
  /** Every label in hanging order: each housing, then its buckets or breakers. */
  labels: FieldLabel[];
  problems: Problem[];
}

const MAX_RUN = 100;
const ROOM = /^(ER|EA|EL)\d{1,2}$/;
const POSITION = /^([A-Z])(\d{1,3})([a-z])?$/;

/**
 * Dictation and quick typing: "ER 1" and "ER-1" are ER1, "MCC 1A", "MCC 1 A" and "MCC dash 1A" are MCC-1A,
 * "A1 to A6" is A1-A6. Dictation's end-of-phrase periods and Smart Punctuation dashes are cleaned up.
 */
export function normalizeLine(raw: string): string[] {
  let t = raw
    .replace(/[\u2010-\u2015\u2212]/g, "-")
    .replace(/[,;]+/g, " ")
    .replace(/\.(?=\s|$)/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toUpperCase();
  t = t.replace(/\s+DASH\s+/g, "-").replace(/\s*-\s*/g, "-");
  t = t.replace(/\b([A-Z]\d{1,3}[A-Z]?)\s+(?:TO|THROUGH|THRU)\s+([A-Z]?\d{1,3})\b/g, "$1-$2");
  t = t.replace(/^(ER|EA|EL)-?(\d{1,2})$/, "$1$2").replace(/^(ER|EA|EL)\s+(\d{1,2})$/, "$1$2");
  const tok = t.split(" ").filter(Boolean);
  if (tok.length >= 3 && HOUSING_TYPES.includes(tok[0]!) && /^\d{1,3}$/.test(tok[1]!) && /^[A-Z]$/.test(tok[2]!)) tok.splice(0, 3, `${tok[0]}-${tok[1]}${tok[2]}`);
  else if (tok.length >= 2 && HOUSING_TYPES.includes(tok[0]!) && /^\d{1,3}[A-Z]?$/.test(tok[1]!)) tok.splice(0, 2, `${tok[0]}-${tok[1]}`);
  return tok;
}

/** "B5A" as typed is the split B5a. Spaces count from 1. */
function positionOf(tok: string): string | null {
  const m = /^([A-Z])(\d{1,3})([A-Z])?$/.exec(tok);
  if (!m || Number(m[2]) < 1) return null;
  return `${m[1]}${Number(m[2])}${m[3] ? m[3].toLowerCase() : ""}`;
}

/** "A1-A6" or "A1-6": the positions in it. Null when it is not a range in one section that counts up from 1 or more. */
function rangeOf(tok: string): string[] | null {
  const m = /^([A-Z])(\d{1,3})-([A-Z])?(\d{1,3})$/.exec(tok);
  if (!m || (m[3] && m[3] !== m[1])) return null;
  const from = Number(m[2]), to = Number(m[4]);
  if (from < 1 || to < from || to - from >= MAX_RUN) return null;
  return Array.from({ length: to - from + 1 }, (_, k) => `${m[1]}${from + k}`);
}

/** The positions written after SPARE, from token j + 1 on. Returns them and the last token used. */
function sparesAfter(tok: readonly string[], j: number): { positions: string[]; end: number } {
  const positions: string[] = [];
  while (tok[j + 1]) {
    const p = positionOf(tok[j + 1]!) ?? null;
    const r = p ? null : rangeOf(tok[j + 1]!);
    if (!p && !r) break;
    positions.push(...(p ? [p] : r!));
    j++;
  }
  return { positions, end: j };
}

const letterOf = (n: number): string => String.fromCharCode(65 + n);
const indexOf = (letter: string): number => letter.charCodeAt(0) - 65;

/** Bucket order: by letter, then space, then split. */
function comparePositions(a: string, b: string): number {
  const ma = POSITION.exec(a)!, mb = POSITION.exec(b)!;
  return ma[1]!.localeCompare(mb[1]!) || Number(ma[2]) - Number(mb[2]) || (ma[3] ?? "").localeCompare(mb[3] ?? "");
}

/** "MCC-1A" as a housing name: its type code. Null when the token is not a housing name. */
function headOf(t: string): RegExpExecArray | null {
  const m = /^([A-Z]+)-([0-9A-Z]{1,4})$/.exec(t);
  return m && HOUSING_TYPES.includes(m[1]!) ? m : null;
}

interface Group {
  tok: string[];
  line: number;
  lastLine: number;
  raw: string;
}

/**
 * The notepad as rooms and housings. A line that is not a room or a housing name carries on the housing above it,
 * so a lineup can be written on one line or one bucket per line (Robert, 2026-10-23, 11:38 AM):
 *   MCC-01             MCC-01 A1-A3 B1 B2
 *   A1                 reads the same
 *   A2 ...
 */
export function groupLines(text: string): Group[] {
  const out: Group[] = [];
  let open: Group | null = null;
  text.split(/\r?\n/).forEach((raw, i) => {
    const line = i + 1;
    const tok = normalizeLine(raw);
    if (!tok.length) return;
    if (tok.length === 1 && ROOM.test(tok[0]!)) {
      open = null;
      out.push({ tok, line, lastLine: line, raw });
      return;
    }
    if (headOf(tok[0]!)) {
      open = { tok, line, lastLine: line, raw };
      out.push(open);
      return;
    }
    if (open) {
      const g: Group = open;
      g.tok.push(...tok);
      g.lastLine = line;
      return;
    }
    out.push({ tok, line, lastLine: line, raw });
  });
  return out;
}

export function parseLineups(text: string): Lineup {
  const rooms: Room[] = [];
  const problems: Problem[] = [];
  let room: Room | null = null;
  /** Per room: the next section letter, and which housing holds each letter. */
  const letters = new Map<string, { next: number; owner: Map<string, string> }>();

  for (const { tok, line, lastLine, raw } of groupLines(text)) {
    if (tok.length === 1 && ROOM.test(tok[0]!)) {
      room = rooms.find((r) => r.name === tok[0]) ?? null;
      if (room) room.line = line;
      if (!room) {
        room = { name: tok[0]!, housings: [], line };
        rooms.push(room);
      }
      continue;
    }
    const head = headOf(tok[0]!);
    if (!head) {
      problems.push({ tone: "amber", line, text: `Line ${line}: "${raw.trim()}" is not a room or a housing. Start with ER1, MCC-1A or PNL-01.` });
      continue;
    }
    if (!room) {
      problems.push({ tone: "amber", line, text: `Line ${line}: ${tok[0]} has no room. Write a room line first, ER1 say.` });
      room = { name: "No Room", housings: [], line: 0 };
      rooms.push(room);
    }
    const r: Room = room;
    const type = head[1]!;
    const name = tok[0]!;
    if (r.housings.some((h) => h.name === name)) problems.push({ tone: "red", line, text: `${r.name} has ${name} twice. One name per housing in a room.` });
    const house: Housing = {
      room: r.name,
      name,
      type,
      label: { key: `${r.name}|${name}|`, room: r.name, housing: name, text: name, kind: BIG_TYPES.includes(type) ? "big" : "housing", section: null, spare: false },
      kids: [],
      lineSize: null,
      main: false,
      line,
      lastLine,
    };

    const spelled: string[] = [];
    const counts: number[] = [];
    const spares: string[] = [];
    for (let j = 1; j < tok.length; j++) {
      const t = tok[j]!;
      const size = /^(\d*\.?\d+)X(\d*\.?\d+)$/.exec(t);
      if (size) {
        const s = sizeFromKey(`${Number(size[1])}x${Number(size[2])}`);
        if (s) house.lineSize = s;
        else problems.push({ tone: "amber", line, text: `${name}: ${t.toLowerCase()} is not a Toro Max size. Use ${TORO_MAX_SIZES.map(sizeName).join(", ")}.` });
        continue;
      }
      if (t === "SPARE" || t === "SPARES") {
        // Only positions follow SPARE. A number after it is never read as a new section.
        const sp = sparesAfter(tok, j);
        if (sp.positions.length) {
          spares.push(...sp.positions);
          j = sp.end;
        } else {
          problems.push({ tone: "amber", line, text: `${name}: SPARE needs a position, SPARE C4 say.` });
          if (tok[j + 1] && /^\d{1,3}$/.test(tok[j + 1]!)) j++;
        }
        continue;
      }
      if (t === "MB" || t === "MAIN") {
        if (BREAKER_TYPES.includes(type)) house.main = true;
        else problems.push({ tone: "amber", line, text: `${name}: MB is for a panel's main breaker. It was not used here.` });
        continue;
      }
      const range = /^([A-Z])(\d{1,3})-([A-Z])?(\d{1,3})$/.exec(t);
      if (range) {
        const run = rangeOf(t);
        if (run) spelled.push(...run);
        else if (range[3] && range[3] !== range[1]) problems.push({ tone: "amber", line, text: `${name}: ${t} runs across two sections. Write each section on its own, ${range[1]}${range[2]}-${range[1]}${range[4]} say.` });
        else problems.push({ tone: "amber", line, text: `${name}: ${t} was not read. Count up from 1, ${range[1]}1-${range[1]}6 say.` });
        continue;
      }
      const pos = positionOf(t);
      if (pos) {
        spelled.push(pos);
        continue;
      }
      if (/^\d{1,3}$/.test(t)) {
        const n = Number(t);
        if (n < 1 || n > MAX_RUN) problems.push({ tone: "amber", line, text: `${name}: ${t} was not read. Use 1 to ${MAX_RUN}.` });
        else counts.push(n);
        continue;
      }
      problems.push({ tone: "amber", line, text: `${name}: "${t}" was not read.` });
    }

    if (isSectioned(type)) {
      const lt = letters.get(r.name) ?? { next: 0, owner: new Map<string, string>() };
      letters.set(r.name, lt);
      // Counted sections take their letters first, from the room's next letter. Spelled positions merge into
      // them: "3 6 B5A B5B" is A1-A3 and B1-B6 with B5 split. A spelled letter past the counts is its own section.
      const all: string[] = [];
      let ranOut = false;
      counts.forEach((n, si) => {
        const idx = lt.next + si;
        if (idx > 25) {
          ranOut = true;
          return;
        }
        for (let k = 1; k <= n; k++) all.push(`${letterOf(idx)}${k}`);
      });
      if (ranOut) problems.push({ tone: "red", line, text: `${name}: ${r.name} has run out of section letters. Check the counts.` });
      all.push(...spelled);
      // A split takes the whole space: B5a and B5b, no B5.
      const kept = all.filter((p) => !(!/[a-z]$/.test(p) && all.some((q) => q !== p && q.startsWith(p) && /^[A-Z]\d+[a-z]$/.test(q) && q.slice(0, -1) === p)));
      const seen = new Set<string>();
      for (const p of kept) {
        if (seen.has(p)) problems.push({ tone: "red", line, key: `${r.name}|${name}|${p}`, text: `${name} has ${p} twice.` });
        seen.add(p);
      }
      const positions = [...seen].sort(comparePositions);
      const used = [...new Set(positions.map((p) => p[0]!))];
      for (const c of used) {
        const other = lt.owner.get(c);
        if (other && other !== name) problems.push({ tone: "red", line, text: `Letter ${c} is on ${other} and ${name} in ${r.name}. A section letter can't repeat in a room.` });
        else lt.owner.set(c, name);
      }
      lt.next = Math.max(lt.next, ...used.map((c) => indexOf(c) + 1));
      for (const c of used) {
        const nums = positions.filter((p) => p[0] === c).map((p) => Number(POSITION.exec(p)![2]));
        const top = Math.max(...nums);
        for (let k = 1; k < top; k++)
          if (!nums.includes(k)) {
            problems.push({ tone: "amber", line, text: `${name} section ${c}: no ${c}${k}. Check for a gap.` });
            break;
          }
        for (const p of positions.filter((x) => x[0] === c && /[a-z]$/.test(x))) {
          const base = p.slice(0, -1);
          if (positions.filter((x) => x.slice(0, -1) === base && /[a-z]$/.test(x)).length < 2) problems.push({ tone: "amber", line, key: `${r.name}|${name}|${p}`, text: `${name}: ${p} is a split with no partner.` });
        }
      }
      for (const s of spares) if (!seen.has(s)) problems.push({ tone: "amber", line, text: `${name}: spare ${s} is not in the lineup.` });
      const sectionOf = new Map(used.map((c, k) => [c, k + 1]));
      house.kids = positions.map((p) => ({ key: `${r.name}|${name}|${p}`, room: r.name, housing: name, text: p, kind: "bucket" as const, section: sectionOf.get(p[0]!) ?? null, spare: spares.includes(p) }));
    } else if (BREAKER_TYPES.includes(type)) {
      if (spelled.length) problems.push({ tone: "amber", line, text: `${name}: positions like ${spelled[0]} are for sections. A panel takes a breaker count, ${name} 30 say.` });
      if (counts.length > 1) problems.push({ tone: "amber", line, text: `${name}: one breaker count per panel. ${counts[0]} was used.` });
      const n = counts[0] ?? 0;
      const kids: FieldLabel[] = [];
      if (house.main) kids.push({ key: `${r.name}|${name}|MB`, room: r.name, housing: name, text: "MB", kind: "breaker", section: null, spare: false });
      for (let k = 1; k <= n; k++) kids.push({ key: `${r.name}|${name}|B${k}`, room: r.name, housing: name, text: `B${k}`, kind: "breaker", section: null, spare: false });
      house.kids = kids;
    } else if (spelled.length || counts.length) {
      problems.push({ tone: "amber", line, text: `${name}: only sections (MCC, switchboard, switchgear) and panel breakers get labels inside a housing. The rest of the line was not used.` });
    }
    if (spares.length && !isSectioned(type)) problems.push({ tone: "amber", line, text: `${name}: spares are marked on sections. SPARE was not used here.` });
    r.housings.push(house);
  }

  const labels = rooms.flatMap((r) => r.housings.flatMap((h) => [h.label, ...h.kids]));
  return { rooms, labels, problems };
}

// ---- Sizes in the field ----

/**
 * Size changes made in the preview, kept with the notepad.
 * Keys: a label's key, or a housing's kids ("ER1|MCC-1A|*"). Values: a size key ("1x4").
 */
export type SizeChoices = Record<string, string>;

export const kidsKey = (room: string, housing: string): string => `${room}|${housing}|*`;

/** The size a label prints at: its own pick, its housing's pick for the kids, the line's size, then the default. */
export function sizeOfLabel(label: FieldLabel, lineup: Lineup, choices: SizeChoices): LabelSize {
  const own = choices[label.key] ? sizeFromKey(choices[label.key]!) : null;
  if (own) return own;
  if (label.kind === "bucket" || label.kind === "breaker") {
    const k = choices[kidsKey(label.room, label.housing)];
    const s = k ? sizeFromKey(k) : null;
    if (s) return s;
  } else {
    const h = lineup.rooms.find((r) => r.name === label.room)?.housings.find((x) => x.name === label.housing);
    if (h?.lineSize) return h.lineSize;
  }
  return DEFAULT_SIZES[label.kind];
}

/** Labels past the 10% shrink on their size, as amber checks. */
export function fitProblems(lineup: Lineup, choices: SizeChoices): Problem[] {
  const out: Problem[] = [];
  for (const r of lineup.rooms)
    for (const h of r.housings) {
      const over = [h.label, ...h.kids].filter((l) => !holdsOn(l, sizeOfLabel(l, lineup, choices)));
      if (!over.length) continue;
      const s = sizeOfLabel(over[0]!, lineup, choices);
      const up = nextSizeUp(over[0]!.text, s);
      out.push({
        tone: "amber",
        line: h.line,
        key: over[0]!.key,
        // Room and gear first, so the line reads right from any room (walk item 11, FIELD_PHASE1_WORDS.md F1c).
        text: `${r.name} ${h.name}${over.length === 1 && over[0] === h.label ? "" : `: ${over.map((l) => l.text).slice(0, 4).join(", ")}${over.length > 4 ? " and more" : ""}`} won't hold full height on ${sizeName(s)}.${up ? ` ${sizeName(up)} fits.` : ""}`,
      });
    }
  return out;
}

export interface PrintFile {
  size: LabelSize;
  labels: FieldLabel[];
}

/** One file per paper size, biggest first (housings, then buckets and breakers). Hanging order inside each. */
export function printFiles(lineup: Lineup, choices: SizeChoices): PrintFile[] {
  const by = new Map<string, PrintFile>();
  for (const l of lineup.labels) {
    const s = sizeOfLabel(l, lineup, choices);
    const k = sizeKey(s);
    if (!by.has(k)) by.set(k, { size: s, labels: [] });
    by.get(k)!.labels.push(l);
  }
  return [...TORO_MAX_SIZES].reverse().map(sizeKey).filter((k) => by.has(k)).map((k) => by.get(k)!);
}

/** "10_Field_Labels_1in_x_4in_2026-10-23.pdf", or "10_ER1_Field_Labels_..." for one room. The paper size to set is in the name. */
export function fieldLabelFileName(siteNumber: string, size: LabelSize, ymd: string, room = ""): string {
  return `${siteNumber}_${room ? `${room}_` : ""}Field_Labels_${size.height}in_x_${size.length}in_${ymd}.pdf`;
}

/** Counts for the page: buckets, spares, breakers in a housing. */
/** What a section's spaces are called: buckets on an MCC, compartments on switchboards and switchgear. */
export const spaceWord = (type: string, n = 2): string => (type === "MCC" ? (n === 1 ? "bucket" : "buckets") : n === 1 ? "compartment" : "compartments");

export function housingSummary(h: Housing): string {
  if (isSectioned(h.type)) {
    const spares = h.kids.filter((k) => k.spare).length;
    return `${h.kids.length} ${spaceWord(h.type, h.kids.length)}${spares ? ` · ${spares} spare` : ""}`;
  }
  if (BREAKER_TYPES.includes(h.type)) {
    const n = h.kids.filter((k) => k.text !== "MB").length;
    return `${n} breaker${n === 1 ? "" : "s"}${h.main ? " with main" : ""}`;
  }
  return "";
}

// ---- Notepad edits: the tap builder and the label menu write the notepad. The notepad stays the one source. ----

/** Where a housing sits in the notepad: its name line and the last line that carries on under it. */
export interface Span {
  line: number;
  lastLine: number;
}

/** Rewrites lines from..to (1 up) from their tokens. Return null to delete the line. Untouched lines keep what was written. */
function rewriteLines(text: string, from: number, to: number, fn: (tok: string[], line: number) => string[] | null): string {
  const lines = text.split(/\r?\n/);
  const out: string[] = [];
  lines.forEach((raw, i) => {
    const n = i + 1;
    if (n < from || n > to || !raw.trim()) {
      out.push(raw);
      return;
    }
    const tok = normalizeLine(raw);
    const next = fn([...tok], n);
    if (next === null) return;
    if (next.join(" ") === tok.join(" ")) out.push(raw);
    else out.push(formatLine(next));
  });
  return out.join("\n");
}

/** Two spaces between parts, one inside a SPARE group: "MCC-01  3  6  SPARE A2 B4". */
function formatLine(tok: readonly string[]): string {
  const k = tok.findIndex((t) => t === "SPARE" || t === "SPARES");
  return k < 0 ? tok.join("  ") : [...tok.slice(0, k), tok.slice(k).join(" ")].join("  ");
}

/** Pulls the SPARE groups out of a line's tokens. */
function splitSpares(tok: readonly string[]): { keep: string[]; spares: string[] } {
  const keep: string[] = [];
  const spares: string[] = [];
  for (let j = 0; j < tok.length; j++) {
    if (tok[j] === "SPARE" || tok[j] === "SPARES") {
      const sp = sparesAfter(tok, j);
      spares.push(...sp.positions);
      j = sp.end;
      continue;
    }
    keep.push(tok[j]!);
  }
  return { keep, spares };
}

const withSpares = (keep: string[], spares: string[]): string[] =>
  spares.length ? [...keep, "SPARE", ...[...new Set(spares)].sort(comparePositions).map((p) => p.toUpperCase())] : keep;

/**
 * Adds or takes one spare. Taking it off finds it on any line of the housing; adding it puts it on the name line.
 * Each changed line is rewritten from its tokens, so taking one off never strands a position or a dash.
 */
export function toggleSpareInText(text: string, at: Span | number, position: string, spare: boolean): string {
  const span: Span = typeof at === "number" ? { line: at, lastLine: at } : at;
  const target = positionOf(position.toUpperCase());
  if (!target) return text;
  return rewriteLines(text, span.line, span.lastLine, (tok, n) => {
    const { keep, spares } = splitSpares(tok);
    const left = spares.filter((p) => p !== target);
    if (spare && n === span.line) left.push(target);
    if (left.length === spares.length && !(spare && n === span.line)) return tok;
    return withSpares(keep, left);
  });
}

/** Adds tokens to the end of a housing's name line: a section count, a size, a split. */
export function appendToHousing(text: string, at: Span, tokens: readonly string[]): string {
  return rewriteLines(text, at.line, at.line, (tok) => {
    const { keep, spares } = splitSpares(tok);
    return withSpares([...keep, ...tokens], spares);
  });
}

/** Takes the last section count off an MCC. */
export function removeLastCount(text: string, at: Span): string {
  for (let n = at.lastLine; n >= at.line; n--) {
    const tok = normalizeLine(text.split(/\r?\n/)[n - 1] ?? "");
    const idx = tok.map((t, k) => (/^\d{1,3}$/.test(t) && tok[k - 1] !== "SPARE" ? k : -1)).filter((k) => k >= 0).pop();
    if (idx !== undefined) return rewriteLines(text, n, n, (t) => t.filter((_, k) => k !== idx));
  }
  return text;
}

/** An MCC's buckets per section, as drawn in the gear grid. A split space counts once. */
export function sectionCounts(h: Housing): number[] {
  const secs = [...new Set(h.kids.map((k) => k.section ?? 0))].sort((a, b) => a - b);
  return secs.map((sct) => new Set(h.kids.filter((k) => k.section === sct).map((k) => k.text.replace(/[a-z]$/, ""))).size);
}

/** The letter each of an MCC's sections carries, in order. */
export function sectionLetters(h: Housing): string[] {
  const secs = [...new Set(h.kids.map((k) => k.section ?? 0))].sort((a, b) => a - b);
  return secs.map((sct) => h.kids.find((k) => k.section === sct)!.text[0]!);
}

/**
 * Sets an MCC's sections from the gear grid: one count per section, top to bottom. The name line becomes
 * the name, its size, the counts, the splits and spares still inside the sections, in that order.
 * Lines carried on under the name are folded in, so the grid and the notepad always agree.
 */
export function setMccSections(text: string, h: Housing, counts: readonly number[]): string {
  const letters = sectionLetters(h);
  // A section set to 0 comes out; the sections after it move up a letter, and their splits and spares move with them.
  const kept = counts.map((n, i) => (n > 0 ? i : -1)).filter((i) => i >= 0);
  const first = letters[0] ? indexOf(letters[0]) : -1;
  const moved = (pos: string): string | null => {
    const m = POSITION.exec(pos);
    if (!m) return null;
    const i = letters.indexOf(m[1]!);
    const k = kept.indexOf(i);
    if (i < 0 || k < 0 || Number(m[2]) > counts[i]!) return null;
    return `${first >= 0 ? letterOf(first + k) : m[1]}${m[2]}${m[3] ?? ""}`;
  };
  const group = groupLines(text).find((g) => g.line === h.line);
  const tok = group?.tok ?? [h.name];
  const size = tok.find((t) => /^(\d*\.?\d+)X(\d*\.?\d+)$/.test(t));
  const splits = h.kids.filter((k) => /[a-z]$/.test(k.text)).map((k) => moved(k.text)).filter((x): x is string => Boolean(x)).map((x) => x.toUpperCase());
  const spares = h.kids.filter((k) => k.spare).map((k) => moved(k.text)).filter((x): x is string => Boolean(x));
  const line = withSpares([h.name, ...(size ? [size] : []), ...kept.map((i) => String(counts[i])), ...splits], spares);
  const lines = text.split(/\r?\n/);
  lines.splice(h.line - 1, h.lastLine - h.line + 1, formatLine(line));
  return lines.join("\n");
}

/** A panel's breaker count: the one number on its lines, replaced. 0 takes it off. */
export function setBreakerCount(text: string, at: Span, count: number): string {
  let t = rewriteLines(text, at.line, at.lastLine, (tok) => tok.filter((x, k) => k === 0 || !/^\d{1,3}$/.test(x)));
  if (count > 0) t = appendToHousing(t, at, [String(count)]);
  return t;
}

/** A panel's main breaker on or off. */
export function setMain(text: string, at: Span, on: boolean): string {
  let t = rewriteLines(text, at.line, at.lastLine, (tok) => tok.filter((x) => x !== "MB" && x !== "MAIN"));
  if (on) t = rewriteLines(t, at.line, at.line, (tok) => [tok[0]!, "MB", ...tok.slice(1)]);
  return t;
}

/** Splits a bucket into a and b, or puts a split bucket back to one. */
export function toggleSplit(text: string, at: Span, position: string): string {
  const p = positionOf(position.toUpperCase());
  if (!p) return text;
  const base = p.replace(/[a-z]$/, "");
  if (/[a-z]$/.test(p)) {
    return rewriteLines(text, at.line, at.lastLine, (tok) => {
      const { keep, spares } = splitSpares(tok);
      const isSplit = (q: string | null) => Boolean(q && /[a-z]$/.test(q) && q.slice(0, -1) === base);
      return withSpares(keep.filter((x) => !isSplit(positionOf(x))), spares.filter((x) => !isSplit(x)));
    });
  }
  return appendToHousing(text, at, [`${base}A`, `${base}B`]);
}

/** Takes a housing and every line under it out of the notepad. */
export function removeHousingLines(text: string, at: Span): string {
  return text
    .split(/\r?\n/)
    .filter((_, i) => i + 1 < at.line || i + 1 > at.lastLine)
    .join("\n");
}

/** Renames a housing on its name line. */
export function renameHousing(text: string, at: Span, name: string): string {
  return rewriteLines(text, at.line, at.line, (tok) => [name, ...tok.slice(1)]);
}

/** The old notepad still reads EL lines (ROOM above) so a carried-over site loads; the page re-codes them to EA. */
export const ROOM_KINDS = ["ER", "EA"] as const;
export type RoomKind = (typeof ROOM_KINDS)[number];

/** The next room of a kind: ER1, then ER2. */
export function nextRoomName(lineup: Lineup, kind: RoomKind): string {
  const used = lineup.rooms.map((r) => new RegExp(`^${kind}(\\d+)$`).exec(r.name)).filter(Boolean).map((m) => Number(m![1]));
  return `${kind}${used.length ? Math.max(...used) + 1 : 1}`;
}

/** Adds a room line at the end. */
export function addRoomLine(text: string, name: string): string {
  const t = text.replace(/\s+$/, "");
  return t ? `${t}\n${name}` : name;
}

/**
 * The name a new housing of a type gets in a room. Panels and the rest count up: PNL-01, PNL-02.
 * MCCs follow the lockout rule: one MCC in a room is MCC-01; adding a second makes them MCC-1A and MCC-1B.
 */
export function nextHousingName(room: Room | null, type: string): { name: string; rename: { from: string; to: string } | null } {
  const same = (room?.housings ?? []).filter((h) => h.type === type).map((h) => h.name);
  if (type === "MCC") {
    if (!same.length) return { name: "MCC-01", rename: null };
    const lettered = same.map((n) => /^MCC-(\d+)([A-Z])$/.exec(n)).filter(Boolean);
    if (!lettered.length) return { name: "MCC-1B", rename: same.includes("MCC-01") ? { from: "MCC-01", to: "MCC-1A" } : null };
    const top = Math.max(...lettered.map((m) => m![2]!.charCodeAt(0)));
    return { name: `MCC-${lettered[0]![1]}${String.fromCharCode(Math.min(top + 1, 90))}`, rename: null };
  }
  const nums = same.map((n) => /-(\d+)$/.exec(n)).filter(Boolean).map((m) => Number(m![1]));
  let n = 1;
  while (nums.includes(n)) n++;
  return { name: `${type}-${String(n).padStart(2, "0")}`, rename: null };
}

/** Adds a housing line at the end of a room's lines (a new room line first when the room is not in the notepad). */
export function addHousingLine(text: string, lineup: Lineup, roomName: string, line: string): string {
  const room = lineup.rooms.find((r) => r.name === roomName);
  if (!room || room.line === 0) return addRoomLine(addRoomLine(text, roomName), line);
  const after = Math.max(room.line, ...room.housings.map((h) => h.lastLine));
  const lines = text.split(/\r?\n/);
  lines.splice(after, 0, line);
  return lines.join("\n").replace(/^\n+/, "");
}
